import React, { useEffect, useState } from "react";
import { ApolloProvider } from "@apollo/client";
import client from "Apollo/config";
import { I18nextProvider } from "react-i18next";
import i18n from "localization/i18n";
import { AppRouter } from "AppRouter";
import { ConfirmProvider } from "components/common/confirm/ConfirmProvider";
import { AppContextWrapper } from "AppContextWrapper";
import { NorreLocalizationProvider } from "components/common/NorreLocalizationProvider";
import { Helmet } from "react-helmet-async";
import { Alert, Box, Snackbar } from "@mui/material";
import {
    DEFAULT_SNACK,
    MessageType,
    Snack,
    SnackbarContext,
} from "Entity/NotistackHandler";
import { NORRE_AUTH_FLASH_STORAGE_KEY } from "Utils/graphqlAuthErrors";
import { isTestEnv } from "Utils/requests";

const SENTRY_URL =
    "https://o4507192475320320.ingest.de.sentry.io/api/4507214576287824/";

const MEDICAL_STORAGE_HOST =
    import.meta.env.MODE === "development"
        ? "http://localhost:4443"
        : isTestEnv()
          ? "https://norre-medical-test.storage.googleapis.com"
          : "https://norre-medical-prod.storage.googleapis.com";

// Node env is set to "production" in both test and prod server, otherwise (locally) it is set to "development".
// NODE_ENV is not good indicator to determine whether app is running in prod or test server.
// const GATEWAY_URL =
//     process.env.NODE_ENV === "production"
//         ? "https://gateway.norre.fi"
//         : "https://gateway-test.norre.fi";

function App() {
    const cspDirectives: string =
        import.meta.env.MODE === "development"
            ? `
              default-src 'self' data:;
              font-src 'self' fonts.gstatic.com;
              img-src 'self' data: ${MEDICAL_STORAGE_HOST} https://flagcdn.com;
              script-src 'self';
              style-src 'self' 'unsafe-inline' fonts.googleapis.com;
              connect-src 'self' localhost:4000 ${MEDICAL_STORAGE_HOST} ${SENTRY_URL};
              worker-src 'self' blob:;
            `
            : `
              default-src 'self' data:;
              font-src 'self' fonts.gstatic.com;
              img-src 'self' data: ${MEDICAL_STORAGE_HOST} https://flagcdn.com;
              script-src 'self';
              style-src 'self' 'unsafe-inline' fonts.googleapis.com;
              connect-src 'self' https://gateway.norre.fi https://gateway-test.norre.fi ${MEDICAL_STORAGE_HOST} ${SENTRY_URL};
              worker-src 'self' blob:;
            `;
    const [snack, setSnack] = useState<Snack>(DEFAULT_SNACK);

    useEffect(() => {
        try {
            const messageKey = sessionStorage.getItem(
                NORRE_AUTH_FLASH_STORAGE_KEY,
            );
            if (messageKey) {
                sessionStorage.removeItem(NORRE_AUTH_FLASH_STORAGE_KEY);
                setSnack({
                    type: MessageType.Err,
                    message: i18n.t(messageKey),
                    open: true,
                });
            }
        } catch {
            // ignore storage access errors
        }
    }, []);

    return (
        <I18nextProvider i18n={i18n}>
            <Helmet>
                <meta
                    httpEquiv="Content-Security-Policy"
                    content={cspDirectives}
                />
            </Helmet>
            <SnackbarContext.Provider value={{ snack, setSnack }}>
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                    }}
                >
                    <ApolloProvider client={client}>
                        <ConfirmProvider>
                            <AppContextWrapper>
                                <NorreLocalizationProvider>
                                    <AppRouter />
                                </NorreLocalizationProvider>
                            </AppContextWrapper>
                        </ConfirmProvider>
                    </ApolloProvider>
                </Box>
                <Snackbar
                    open={snack.open}
                    autoHideDuration={5000}
                    onClose={() => setSnack(DEFAULT_SNACK)}
                    message={snack.message}
                >
                    <Alert
                        onClose={() => setSnack(DEFAULT_SNACK)}
                        severity={snack.type}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {snack.message}
                    </Alert>
                </Snackbar>
            </SnackbarContext.Provider>
        </I18nextProvider>
    );
}

export default App;
